import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useConsent } from '../../hooks/useConsent'
import { PRIVACY_POLICY, TERMS_OF_SERVICE } from '../../data/legalDocuments'
import type { LegalDocument } from '../../types/consent'
import LegalDocView from './LegalDocView'

/**
 * Admin-side "Privacy & legal" section. Shows the admin user the ToS and
 * Privacy Policy versions they've accepted, with click-to-view modals.
 * No DPA — that flow lives in the manager app for org admins.
 */
export default function PrivacyLegalSection() {
  const { loading, userConsent } = useConsent()
  const [viewing, setViewing] = useState<LegalDocument | null>(null)

  return (
    <div className="bg-white dark:bg-gray-800/50 dark:backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-transparent p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
          <ShieldCheckIcon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Privacy &amp; legal
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Review the policies you've accepted and read the current versions
          </p>
        </div>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
        <DocRow
          doc={TERMS_OF_SERVICE}
          acceptedVersion={userConsent?.tos_version}
          acceptedAt={userConsent?.accepted_at}
          loading={loading}
          onView={() => setViewing(TERMS_OF_SERVICE)}
        />
        <DocRow
          doc={PRIVACY_POLICY}
          acceptedVersion={userConsent?.privacy_version}
          acceptedAt={userConsent?.accepted_at}
          loading={loading}
          onView={() => setViewing(PRIVACY_POLICY)}
        />
      </ul>

      <p className="mt-4 text-[11px] text-gray-400 dark:text-gray-500">
        When we publish a new version of any policy, you'll be re-prompted on
        next sign-in.
      </p>

      {viewing && <DocViewerModal doc={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}

interface DocRowProps {
  doc: LegalDocument
  acceptedVersion?: string
  acceptedAt?: Date
  loading: boolean
  onView: () => void
}

function DocRow({ doc, acceptedVersion, acceptedAt, loading, onView }: DocRowProps) {
  const accepted = !!acceptedVersion
  const upToDate = acceptedVersion === doc.version

  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <DocumentTextIcon className="w-5 h-5 mt-0.5 text-gray-400 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {labelFor(doc.type)}
            </p>
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Current: {doc.version}
            </span>
            {accepted && !upToDate && (
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                Update available
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {loading
              ? 'Loading…'
              : accepted
                ? `Accepted on ${formatDate(acceptedAt)} · version ${acceptedVersion}`
                : 'Not yet accepted'}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onView}
        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 flex-shrink-0"
      >
        View
      </button>
    </li>
  )
}

interface ModalProps {
  doc: LegalDocument
  onClose: () => void
}

function DocViewerModal({ doc, onClose }: ModalProps) {
  const titleId = useId()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh] outline-none"
      >
        <div className="px-6 py-4 flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Version {doc.version} · Effective {doc.effective_date}
            </p>
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {labelFor(doc.type)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <LegalDocView doc={doc} />
        </div>
      </div>
    </div>,
    document.body
  )
}

function labelFor(type: LegalDocument['type']): string {
  switch (type) {
    case 'tos':
      return 'Terms of Service'
    case 'privacy':
      return 'Privacy Policy'
  }
}

function formatDate(d?: Date): string {
  if (!d) return ''
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
